import classNames from "classnames"

import LayoutItem from "../Layouts/LayoutItem"

import Link from "@components/elements/Link"
import Badge from "@components/elements/V2/Badge"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import { formatPrice } from "@utils/price"
import {
  getAccentColor,
  getBackgroundColorClass,
  getGradientFromColorClass,
  getGradientToColorClass,
  getTextColorClass,
} from "@utils/V2/color"
import React, { ElementType } from "react"

type Props = {
  title: string
  superscriptText?: string
  description?: string
  image?: string
  imageAlt?: string
  isBackgroundImage?: boolean
  badge?: Storyblok.Badge
  price?: number
  priceSuperscript?: string
  ctaText?: string
  ctaColor?: Color
  ctaLink?: string | YocoCom.YocoLink
  backgroundColor: Color
  secondaryBackgroundColor?: Color
  size?: "small" | "large"
  mobileGridOrder?: number
}

const IntroCard = ({
  title,
  superscriptText,
  description,
  image,
  imageAlt,
  isBackgroundImage = false,
  badge,
  price,
  priceSuperscript,
  ctaText,
  ctaColor,
  ctaLink,
  backgroundColor,
  secondaryBackgroundColor,
  size = "small",
  mobileGridOrder,
  ...props
}: Props) => {
  const textColor = isBackgroundImage
    ? Color.White
    : getAccentColor(backgroundColor)

  const resolvedCtaColor = isBackgroundImage
    ? Color.White
    : (ctaColor ?? getAccentColor(backgroundColor))

  const numericPrice =
    price === undefined || price === null || `${price}`.trim() === ""
      ? undefined
      : Number(price)

  const hasValidPrice =
    numericPrice !== undefined && Number.isFinite(numericPrice)

  const colSpanClasses =
    size === "large"
      ? "col-span-full sm:col-span-2 md:col-span-3 lg:col-span-6"
      : "col-span-full sm:col-span-2 md:col-span-3 lg:col-span-4"

  const cardClasses = classNames(
    "group relative flex flex-col overflow-hidden rounded",
    colSpanClasses,
    { "cursor-pointer": !!ctaLink },
    !isBackgroundImage &&
      (secondaryBackgroundColor
        ? [
            "bg-gradient-to-br",
            getGradientFromColorClass(backgroundColor),
            getGradientToColorClass(secondaryBackgroundColor),
          ]
        : getBackgroundColorClass(backgroundColor))
  )

  const Wrapper = (ctaLink ? Link : "div") as ElementType

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as={Wrapper}
      className={cardClasses}
      animateOnScroll
      {...(ctaLink ? { link: ctaLink } : {})}
      {...props}
    >
      {isBackgroundImage && image && (
        <>
          <div className="absolute inset-0">
            <GatsbyStoryblokImage
              image={image}
              alt={imageAlt ?? ""}
              loadType="eager"
              quality={80}
              aspectRatio="fullWidth"
              imageObjectFit="cover"
              className="h-full w-full"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-br from-charcoal/80 via-charcoal/40 to-transparent" />
        </>
      )}

      <div className="relative z-10 flex min-h-[162px] flex-col gap-10 px-40 pt-40">
        {superscriptText && (
          <Typography
            text={superscriptText}
            size="subscript-lg"
            font="grotesk"
            weight="medium"
            color={textColor}
            className={classNames("uppercase", {
              "opacity-80": !isBackgroundImage,
            })}
          />
        )}
        <Typography
          text={title}
          size="lead-sm"
          font="grotesk"
          weight="medium"
          color={textColor}
        />
        {description && (
          <Typography
            text={description}
            size="body-md"
            font="grotesk"
            weight="book"
            color={textColor}
            className={classNames({ "opacity-80": !isBackgroundImage })}
          />
        )}
        {ctaText && (
          <div
            className={classNames(
              "mt-16 flex items-center gap-8",
              getTextColorClass(resolvedCtaColor)
            )}
          >
            <Typography
              text={ctaText}
              size="body-md"
              font="grotesk"
              weight="medium"
              color={resolvedCtaColor}
              leadingNone
            />
            <ArrowIcon />
          </div>
        )}
      </div>

      <div
        className={classNames("relative z-10 mt-auto aspect-[389/292] w-full", {
          "lg:mx-auto lg:w-2/3": size === "large",
        })}
      >
        {!isBackgroundImage && image && (
          <GatsbyStoryblokImage
            image={image}
            alt={imageAlt ?? ""}
            aspectRatio="4:3"
            imageObjectFit="cover"
            className="pointer-events-none h-full w-full"
          />
        )}
      </div>

      {(hasValidPrice || badge?.text) && (
        <div className="absolute inset-x-0 bottom-[30px] z-20 flex items-center justify-between pl-40 pr-[30px]">
          <div className="relative">
            {hasValidPrice && (
              <>
                {priceSuperscript && (
                  <Typography
                    text={priceSuperscript}
                    size="lead-sm"
                    font="grotesk"
                    weight="medium"
                    color={textColor}
                    className="absolute bottom-full left-0"
                  />
                )}
                <Typography
                  text={formatPrice(numericPrice as number, {
                    hasThousandsSeparator: true,
                  }).replace("R ", "R")}
                  size="h4"
                  font="grotesk"
                  weight="medium"
                  color={textColor}
                />
              </>
            )}
          </div>

          {badge?.text && (
            <Badge
              text={badge.text}
              highlightedText={badge.highlightedText}
              textColor={badge.textColor ?? getAccentColor(badge.color)}
              accentColor={badge.color}
              uppercaseText={false}
              style="solid"
              isRounded
            />
          )}
        </div>
      )}
    </LayoutItem>
  )
}

export default IntroCard
